/** @jsx h */
/** @jsxFrag Fragment */
import { h } from "preact";
import { useState } from "preact/hooks";
import { useSelect } from "@uiw/utils";
import { DatGui, DatNumber, DatSubFolder, useDatFolderDispatch } from "@uiw/dat";
import { ChartThemeDark, WaveformChart } from "@wbaudio/ui/chart";
import { SampleGenSynthNames } from "@wbaudio/core/synth";
import { ActivePreset, useShushApp, useShushAppSelect } from "@/spa/app/app.tsx";
import { LayerEditor } from "./layer.tsx";
import { FxList } from "./fxList.tsx";
import { DatRow, RowButton } from "./row.tsx";

const WaveWidth = 320;
const WaveHeight = 60;

export interface PreviewControlsProps {}
export function PreviewControls(props: PreviewControlsProps) {
  const { preview } = useShushApp();
  const status = useSelect(preview, (s) => s.status);
  const loop = useSelect(preview, (s) => s.loop);
  const error = useSelect(preview, (s) => s.error);

  return (
    <div class="config-transport">
      <span class="config-label">preview</span>
      {status === "playing"
        ? <RowButton onClick={() => preview.dispatch({ type: "stop" })}>stop</RowButton>
        : <RowButton onClick={() => preview.dispatch({ type: "play" })}>play</RowButton>}
      <RowButton onClick={() => preview.dispatch({ type: "restart" })}>restart</RowButton>
      <RowButton
        active={loop}
        onClick={() => preview.dispatch({ type: "loop", payload: { loop: !loop } })}
      >
        loop
      </RowButton>
      {error && <span class="config-error">{error}</span>}
    </div>
  );
}

export interface SaveBarProps {}
export function SaveBar(props: SaveBarProps) {
  const app = useShushApp();
  const name = useShushAppSelect((s) => ActivePreset(s).name);
  const dirty = useShushAppSelect((s) => s.dirty);
  const loop = useShushAppSelect((s) => s.loop);
  const error = useShushAppSelect((s) => s.error);

  return (
    <div class="config-save">
      <span class="config-label">
        {name}
        {dirty ? " · unsaved" : ""}
      </span>
      <RowButton onClick={() => app.dispatch({ type: "edit/reset" })}>reset to defaults</RowButton>
      <RowButton active={dirty} onClick={() => app.dispatch({ type: "save" })}>
        {loop === "rendering" ? "saving…" : "save"}
      </RowButton>
      {loop === "error" && error && <span class="config-error">{error}</span>}
    </div>
  );
}

export interface ConfigViewProps {}
export function ConfigView(props: ConfigViewProps) {
  const app = useShushApp();
  const { gen } = app;
  const state = useSelect(gen, (s) => s);
  const samples = useShushAppSelect((s) => s["loop/samples"]);
  const folders = useDatFolderDispatch({ defaultOpen: true });
  const [synth, setSynth] = useState("shush");

  return (
    <div class="config-view">
      <SaveBar />
      <PreviewControls />
      <div class="config-wave">
        <WaveformChart
          samples={samples}
          sampleRate={state.sampleRate}
          width={WaveWidth}
          height={WaveHeight}
          theme={ChartThemeDark}
          axis={false}
        />
      </div>
      <DatGui styleOpts={{ "size/width": WaveWidth }}>
        <DatSubFolder folders={folders} path="/sample" title="sample" hasSubfolders>
          <DatNumber
            label="length (s)"
            value={state.length}
            min={0.1}
            max={60}
            step={0.1}
            onChange={(length) => gen.dispatch({ type: "length", payload: { length } })}
          />
          <DatNumber
            label="gain"
            value={state.gain}
            min={0}
            max={4}
            step={0.01}
            onChange={(gain) => gen.dispatch({ type: "gain", payload: { gain } })}
          />
          <DatRow label="add layer">
            <select value={synth} onChange={(e) => setSynth(e.currentTarget.value)}>
              {SampleGenSynthNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <RowButton onClick={() => gen.dispatch({ type: "layer/add", payload: { synth } })}>
              add
            </RowButton>
          </DatRow>
          {state.layers.map((layer, i) => (
            <LayerEditor
              key={layer.id}
              gen={gen}
              layer={layer}
              index={i}
              count={state.layers.length}
              length={state.length}
              folders={folders}
              path={`/sample/${layer.id}`}
            />
          ))}
          <FxList
            slots={state.fx}
            folders={folders}
            path="/sample/fx"
            title="master effects"
            actions={{
              add: (type) => gen.dispatch({ type: "fx/add", payload: { type } }),
              remove: (slot) => gen.dispatch({ type: "fx/remove", payload: { slot } }),
              move: (slot, by) => gen.dispatch({ type: "fx/move", payload: { slot, by } }),
              bypass: (slot) => gen.dispatch({ type: "fx/bypass", payload: { slot } }),
              opts: (slot, opts) => gen.dispatch({ type: "fx/opts", payload: { slot, opts } }),
            }}
          />
        </DatSubFolder>
      </DatGui>
    </div>
  );
}
